import React, { useEffect, useMemo, useState } from 'react'
import { Tooltip, TypoGraph } from 'components'
import strings from 'strings'
import styles from './claimsFilters.module.scss'
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  FormHelperText,
  Grid,
  TextField,
} from '@mui/material'
import moment, { Moment } from 'moment'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { SafetyEventType, VehicleDetails } from 'interfaces/IClaimsSearching'

interface IClaimsFiltersProps {
  carrierView?: boolean
  carrierSelected: boolean
  safetyEventsList: SafetyEventType[]
  vehicleList: VehicleDetails[]
  carrierList: any[]
  searchBtnLoading: boolean
  carriersLoading: boolean
  safetyEventsLoading: boolean
  vehiclesLoading: boolean
  onSafetySearch: (payload: any) => void
  onCarrierSelect: (carrier: any) => void
  resetSafetyAndRoutesList: () => void
  fixedCarrierId?: number | null
  fixedCarrierName?: string
}

const cs = strings.CLAIMS_SEARCHING
const t = strings.CLAIMS_SEARCHING

const ClaimsFilters: React.FC<IClaimsFiltersProps> = ({
  carrierView = false,
  carrierSelected,
  safetyEventsList,
  vehicleList,
  carrierList,
  searchBtnLoading,
  carriersLoading,
  safetyEventsLoading,
  vehiclesLoading,
  onSafetySearch,
  onCarrierSelect,
  resetSafetyAndRoutesList,
  fixedCarrierId,
  fixedCarrierName,
}) => {
  const [loading, setLoading] = useState(true)
  const [selectedSafetyEventsTypes, setSelectedSafetyEventsTypes] = useState<
    any[]
  >([])
  const [selectedVehicles, setSelectedVehicles] = useState<any[]>([])
  const [selectedCarrier, setSelectedCarrier] = useState<any>(null)
  const [startDate, setStartDate] = useState<Moment | null>(null)
  const [endDate, setEndDate] = useState<Moment | null>(null)
  const [endDateError, setEndDateError] = useState<boolean>(false)

  const [searchBtnEnabled, setSearchBtnEnabled] = useState<boolean>(false)

  const sortedSafetyEventsTypes = useMemo(() => {
    return [...safetyEventsList]
      .map((item) => {
        const firstLetter = item?.eventType[0].toUpperCase()
        return {
          ...item,
          firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
        }
      })
      .sort((a, b) => a.eventType?.localeCompare(b?.eventType))
  }, [safetyEventsList])

  const sortedVehicleList = useMemo(() => {
    return [...vehicleList]
      .map((item) => {
        const firstLetter = item?.vehicleId?.assetEldId[0].toUpperCase()
        return {
          ...item,
          firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
        }
      })
      .sort((a, b) =>
        a.vehicleId?.assetEldId.localeCompare(b?.vehicleId?.assetEldId)
      )
  }, [vehicleList])

  const sortedCarrierList = useMemo(() => {
    return [...carrierList]
      .map((item) => {
        const firstLetter = item?.carrierName[0].toUpperCase()
        return {
          ...item,
          firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
        }
      })
      .sort((a, b) => a.carrierName?.localeCompare(b?.carrierName))
  }, [carrierList])

  const fixedCarrierOption = carrierView && fixedCarrierId != null && fixedCarrierName != null
    ? { carrierId: fixedCarrierId, carrierName: fixedCarrierName, firstLetter: fixedCarrierName[0]?.toUpperCase() ?? '0-9' }
    : null

  useEffect(() => {
    const now = moment().utc()
    const oneWeekAgo = moment().utc().subtract(7, 'days')
    setStartDate(oneWeekAgo)
    setEndDate(now)
    setEndDateError(false)
  }, [])

  useEffect(() => {
    if (
      carrierSelected &&
      Array.isArray(sortedVehicleList) &&
      sortedVehicleList.length > 0 &&
      Array.isArray(sortedSafetyEventsTypes)
    ) {
      if (!startDate || !endDate) {
        const now = moment().utc()
        const oneWeekAgo = moment().utc().subtract(7, 'days')
        setStartDate(oneWeekAgo)
        setEndDate(now)
        setEndDateError(false)
      }
    }
  }, [
    carrierSelected,
    sortedSafetyEventsTypes,
    sortedVehicleList,
    startDate,
    endDate,
  ])

  useEffect(() => {
    const hasValidDates = !!(startDate && endDate && !endDateError)
    setSearchBtnEnabled(hasValidDates)
  }, [startDate, endDate, endDateError])

  const formatDates = (startDate: any, endDate: any) => ({
    startDate: startDate.utc().format('YYYY-MM-DD[T]HH:mm:ss'),
    endDate: endDate.utc().format('YYYY-MM-DD[T]HH:mm:ss'),
  })

  const buildVehicleIds = (selectedVehicles: any[]) =>
    selectedVehicles.map((vehicle) => ({
      companyEldAuthenticationId:
        vehicle?.vehicleId?.companyEldAuthenticationId,
      assetEldId: vehicle?.vehicleId.assetEldId,
    }))

  const handleSearchVehicles = () => {
    if (!startDate || !endDate || endDateError) {
      return
    }

    const { startDate: formattedStartDate, endDate: formattedEndDate } =
      formatDates(startDate, endDate)
    const vehicleIds =
      selectedVehicles.length > 0 ? buildVehicleIds(selectedVehicles) : []

    resetSafetyAndRoutesList()

    const selectedEventTypes = selectedSafetyEventsTypes.map(
      (event) => event?.type
    )
    const payload = {
      carrierId: carrierView && fixedCarrierId != null ? fixedCarrierId : selectedCarrier?.carrierId,
      vehicleIds,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      types: selectedEventTypes ?? [],
      includeEmptyCoordinates: false,
      limit: carrierSelected
        ? cs.SAFETY_EVENTS_CARRIER_LIMIT
        : cs.SAFETY_EVENTS_PORTFOLIO_LIMIT,
    }
    onSafetySearch(payload)
  }

  const handleSafetyEventsFilter = (
    event: any,
    newValue: SafetyEventType[]
  ) => {
    setSelectedSafetyEventsTypes(newValue)
  }

  const handleVehiclesFilter = (event: any, newValue: VehicleDetails[]) => {
    setSelectedVehicles(newValue)
  }

  const handleCarrierSelection = (event: any, newValue: any) => {
    if (newValue) {
      setSelectedCarrier(newValue)
      onCarrierSelect(newValue)
    } else {
      setSelectedCarrier(null)
      onCarrierSelect(null)
    }
  }

  const handleStartDateChange = (newValue: moment.Moment | null) => {
    setStartDate(newValue)

    if (endDate && newValue) {
      if (endDate?.isBefore(newValue) || endDate?.isSame(newValue)) {
        setEndDate(newValue.clone().add(1, 'minute'))
        setEndDateError(false)
      }
    }
  }

  const handleEndDateChange = (newValue: moment.Moment | null) => {
    if (newValue && startDate) {
      if (startDate && newValue.diff(startDate, 'minute') < 1) {
        setEndDateError(true)
        return
      }
      setEndDateError(false)
    }
    setEndDate(newValue)
  }

  return (
    <Card data-testid="claims-filters-section" variant="outlined" sx={{ marginBottom: 2, padding: 2 }}>
      <Grid container spacing={2} mb={2}>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          display={'flex'}
          alignItems="center"
          gap={2}
        >
          <Autocomplete
            id="carrierOptions"
            sx={{ width: '100%' }}
            options={carrierView ? (fixedCarrierOption ? [fixedCarrierOption] : []) : sortedCarrierList}
            groupBy={(option) => option?.firstLetter}
            getOptionLabel={(option) => option?.carrierName}
            value={carrierView ? fixedCarrierOption : selectedCarrier}
            onChange={handleCarrierSelection}
            loading={carriersLoading}
            disabled={carrierView || carriersLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t.CARRIERS_SEARCH}
                placeholder={
                  carriersLoading ? 'Loading carriers...' : t.CARRIERS_SEARCH
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {carriersLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          display={'flex'}
          alignItems="center"
          gap={2}
        >
          <Autocomplete
            multiple
            id="vehiclesOptions"
            sx={{ width: '100%' }}
            options={sortedVehicleList}
            groupBy={(option) => option?.firstLetter}
            getOptionLabel={(option) => {
              return option?.vin
                ? `${option?.vehicleId?.assetEldId} (${option?.vin})`
                : `${option?.vehicleId?.assetEldId} (-)`
            }}
            filterSelectedOptions={true}
            value={selectedVehicles}
            onChange={handleVehiclesFilter}
            loading={vehiclesLoading}
            disabled={vehiclesLoading}
            renderOption={(props, option) => {
              const optionKey = `${option?.vehicleId?.assetEldId}-${option?.vin}`
              return (
                <li {...props} key={optionKey}>
                  {option?.vin
                    ? `${option?.vehicleId?.assetEldId} (${option?.vin})`
                    : `${option?.vehicleId?.assetEldId} (-)`}
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t.SEARCH_VEHICLES}
                placeholder={
                  vehiclesLoading ? 'Loading vehicles...' : t.SEARCH_VEHICLES
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {vehiclesLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          display={'flex'}
          alignItems="center"
          gap={2}
        >
          <Tooltip
            title={t.NO_SAFETY_TOOLTIP}
            containerClassName={styles.tooltipDisabled}
            sx={{ background: '100%' }}
            show={false}
          >
            <Autocomplete
              id="SafetyEventsTypesOptions"
              multiple
              sx={{ width: '100%' }}
              options={sortedSafetyEventsTypes}
              groupBy={(option) => option?.firstLetter}
              getOptionLabel={(option) => option?.eventType}
              filterSelectedOptions
              value={selectedSafetyEventsTypes}
              onChange={handleSafetyEventsFilter}
              loading={safetyEventsLoading}
              disabled={safetyEventsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t.SEARCH_SAFETY_LABEL}
                  placeholder={
                    safetyEventsLoading
                      ? 'Loading safety events...'
                      : t.SEARCH_SAFETY_LABEL
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {safetyEventsLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Tooltip>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          display={'flex'}
          alignItems="center"
          justifyContent={'space-between'}
          gap={1}
        >
          <Grid>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                label={t.START_DATE_LABEL + ' (UTC)'}
                value={startDate}
                onChange={handleStartDateChange}
                maxDateTime={
                  endDate ? endDate.clone().subtract(1, 'minute') : undefined
                }
                disabled={!carrierSelected}
              />
            </LocalizationProvider>
          </Grid>
          <TypoGraph variant="h2" mb={0}>
            {'-'}
          </TypoGraph>
          <Grid>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                label={t.END_DATE_LABEL + ' (UTC)'}
                value={endDate}
                onChange={handleEndDateChange}
                minDateTime={
                  startDate ? startDate.clone().add(1, 'minute') : undefined
                }
                disabled={!startDate || !carrierSelected}
              />
            </LocalizationProvider>
            {endDateError && (
              <FormHelperText error>{t.END_DATE_ERROR}</FormHelperText>
            )}
          </Grid>
          <Grid>
            <Button
              data-testid="claims-search-btn"
              sx={{ borderRadius: 25, minWidth: 120 }}
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSearchVehicles}
              disabled={!searchBtnEnabled || searchBtnLoading}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {searchBtnLoading && (
                  <CircularProgress
                    size={16}
                    color="inherit"
                    sx={{ marginRight: 1 }}
                  />
                )}
                {t.SEARCH}
              </Box>
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  )
}

export default ClaimsFilters
